import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight,
  CloudRain,
  Wrench,
  PaintBucket,
  Building2,
  ShieldAlert,
  Award,
  Users,
  Clock,
  CheckCircle2,
  Zap,
  BadgeCheck
} from 'lucide-react';

const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

export default function Home() {
  const aboutRef = useRef(null);
  const servicesRef = useRef(null);
  const whyRef = useRef(null);

  const aboutInView = useInView(aboutRef, { once: true, margin: '-100px' });
  const servicesInView = useInView(servicesRef, { once: true, margin: '-100px' });
  const whyInView = useInView(whyRef, { once: true, margin: '-100px' });

  const divisions = [
    {
      icon: CloudRain,
      title: 'Roofing & Waterproofing',
      description:
        'Structural timber restoration, advanced waterproofing systems, and full-building solutions.',
      link: '/services#roofing'
    },
    {
      icon: Wrench,
      title: 'Plumbing Services',
      description:
        'Leak detection, geyser replacements, and complete plumbing services. PIRB Reg #2925/10.',
      link: '/services#plumbing'
    },
    {
      icon: PaintBucket,
      title: 'General Maintenance',
      description: 'Painting, corrective and preventative property maintenance.',
      link: '/services#maintenance'
    },
    {
      icon: Building2,
      title: 'Building Construction',
      description: 'New builds, renovations, structural and cosmetic repairs.',
      link: '/services#construction'
    },
    {
      icon: ShieldAlert,
      title: 'Type 3 Asbestos Removal',
      description: 'Certified specialist division for high-risk asbestos removal.',
      link: '/services#asbestos'
    }
  ];

  const whyChooseUs = [
    {
      icon: Clock,
      title: '30+ Years Experience',
      description: 'Collective insurance damage reinstatement expertise across our key personnel.'
    },
    {
      icon: Zap,
      title: 'Fast-Track Service',
      description: '5 mobile teams operating daily, each led by a dedicated Supervisor.'
    },
    {
      icon: BadgeCheck,
      title: 'Full Compliance',
      description: 'COIDA compliant with comprehensive public liability insurance coverage.'
    },
    {
      icon: Award,
      title: 'Level 1 B-BBEE',
      description: '100% Black-Owned, certified Level 1 B-BBEE contributor.'
    }
  ];

  return (
    <main data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/g3o47knq_working%20guys%201.jpg"
            alt="MB Construction team at work"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/70 to-background" />
        </div>

        {/* Grid Lines Overlay */}
        <div className="absolute inset-0 grid-lines opacity-30" />

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="overline mb-6 block">Specialist Insurance Repair // South Africa</span>
            <h1 className="font-heading font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-text-main tracking-tight">
              BUILDING<br />
              <span className="text-primary">PARTNERSHIPS.</span>
            </h1>
            <h2 className="font-heading font-light text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-text-muted mt-4 tracking-tight italic">
              Delivering Quality.
            </h2>
            <p className="mt-8 max-w-2xl mx-auto text-text-muted font-body text-base md:text-lg leading-relaxed">
              Specialist Insurance Repair & Building Construction Partner serving the Western Cape
              and nationally. Five integrated divisions. One trusted partner.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.5 }}
            className="mt-12 flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link
              to="/services"
              data-testid="hero-services-btn"
              className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2"
            >
              Our Services
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/contact"
              data-testid="hero-get-quote-btn"
              className="border border-text-muted/30 text-text-main px-8 py-4 font-mono text-xs uppercase tracking-widest hover:border-text-main transition-colors inline-flex items-center justify-center"
            >
              Get a Quote
            </Link>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
        >
          <div className="w-[1px] h-16 bg-gradient-to-b from-transparent via-primary to-transparent" />
        </motion.div>
      </section>

      {/* About Section */}
      <section
        ref={aboutRef}
        data-testid="about-summary-section"
        className="py-24 md:py-32 lg:py-48 bg-background"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              animate={aboutInView ? 'visible' : 'hidden'}
            >
              <motion.span variants={fadeUpVariants} className="overline">
                01 // About MB Construction
              </motion.span>
              <motion.h2
                variants={fadeUpVariants}
                className="font-heading font-light text-4xl md:text-5xl lg:text-6xl text-text-main mt-4 tracking-tight"
              >
                A Partner You<br />
                <span className="text-primary">Can Build On</span>
              </motion.h2>
              <motion.div
                variants={fadeUpVariants}
                className="mt-8 space-y-5 text-text-muted font-body leading-relaxed"
              >
                <p>
                  MBConstruction is a <span className="text-text-main">100% Black-Owned, Level 1 B-BBEE</span>{' '}
                  specialist contractor serving the South African insurance, intermediary, and
                  construction markets.
                </p>
                <p>
                  We combine over <span className="text-text-main">30 years of collective experience</span>{' '}
                  from our key personnel with modern claim management technology to deliver efficient,
                  transparent, and high-quality repair and construction solutions.
                </p>
              </motion.div>

              <motion.ul variants={fadeUpVariants} className="mt-8 space-y-3">
                {[
                  'Turnkey approach reduces claim lifecycle times',
                  'Enhances policyholder satisfaction',
                  'Major projects across Western Cape and nationally'
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-text-muted text-sm">
                    <CheckCircle2 size={16} className="text-primary flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </motion.ul>

              <motion.div variants={fadeUpVariants} className="mt-10">
                <Link
                  to="/about"
                  data-testid="about-learn-more-btn"
                  className="inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest hover:gap-4 transition-all"
                >
                  Learn More About Us
                  <ArrowRight size={14} />
                </Link>
              </motion.div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={aboutInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_mobile-first-build-48/artifacts/twvbzukk_Work%20Background.jpg"
                  alt="MB Construction work site"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-8 -left-8 bg-surface border border-border p-8">
                <div className="font-heading font-light text-5xl text-primary">30+</div>
                <div className="font-mono text-xs uppercase tracking-widest text-text-muted mt-2">
                  Years of Experience
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Divisions / Services Section */}
      <section
        ref={servicesRef}
        data-testid="divisions-section"
        className="py-24 md:py-32 lg:py-48 bg-surface border-y border-border"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={servicesInView ? 'visible' : 'hidden'}
          >
            <motion.span variants={fadeUpVariants} className="overline">
              02 // Our Divisions
            </motion.span>
            <motion.h2
              variants={fadeUpVariants}
              className="font-heading font-light text-4xl md:text-5xl lg:text-6xl text-text-main mt-4 tracking-tight"
            >
              Five Integrated<br />
              <span className="text-primary">Divisions</span>
            </motion.h2>
            <motion.p
              variants={fadeUpVariants}
              className="mt-6 max-w-2xl text-text-muted font-body text-base leading-relaxed"
            >
              Comprehensive insurance repair and building construction services delivered through
              specialist divisions, each led by experienced supervisors.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={servicesInView ? 'visible' : 'hidden'}
            className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
          >
            {divisions.map((service, index) => (
              <motion.div
                key={service.title}
                variants={fadeUpVariants}
                className="service-card group bg-background border border-border p-8 md:p-10"
              >
                <service.icon className="text-primary mb-6" size={32} strokeWidth={1} />
                <h3 className="font-heading text-2xl md:text-3xl text-text-main mb-4 tracking-tight">
                  {service.title}
                </h3>
                <p className="text-text-muted font-body text-sm leading-relaxed mb-6">
                  {service.description}
                </p>
                <Link
                  to={service.link}
                  data-testid={`division-link-${index}`}
                  className="inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest group-hover:gap-4 transition-all"
                >
                  Learn More
                  <ArrowRight size={14} />
                </Link>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="inline-flex items-center gap-2 text-primary font-mono text-xs uppercase tracking-widest hover:gap-4 transition-all"
            >
              View All Services
              <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section
        ref={whyRef}
        data-testid="why-section"
        className="py-24 md:py-32 lg:py-48 bg-background"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={whyInView ? 'visible' : 'hidden'}
          >
            <motion.span variants={fadeUpVariants} className="overline">
              03 // Why Choose Us
            </motion.span>
            <motion.h2
              variants={fadeUpVariants}
              className="font-heading font-light text-4xl md:text-5xl lg:text-6xl text-text-main mt-4 tracking-tight"
            >
              Why Insurers, Brokers<br />
              <span className="text-primary">& Developers Choose Us</span>
            </motion.h2>
            <motion.p
              variants={fadeUpVariants}
              className="mt-6 max-w-2xl text-text-muted font-body text-base leading-relaxed"
            >
              Our commitment to excellence, transparency, and fast-track service makes us the
              preferred partner for institutional and commercial clients.
            </motion.p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={whyInView ? 'visible' : 'hidden'}
            className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {whyChooseUs.map((item) => (
              <motion.div
                key={item.title}
                variants={fadeUpVariants}
                className="bg-surface border border-border p-8 md:p-10"
              >
                <item.icon className="text-primary mb-6" size={32} strokeWidth={1} />
                <h3 className="font-heading text-xl md:text-2xl text-text-main mb-3 tracking-tight">
                  {item.title}
                </h3>
                <p className="text-text-muted font-body text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        data-testid="cta-section"
        className="py-24 md:py-32 lg:py-48 bg-surface border-t border-border"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <span className="overline">Start Your Project</span>
            <h2 className="font-heading font-light text-4xl md:text-5xl lg:text-6xl text-text-main mt-4 tracking-tight">
              Ready to Start<br />
              <span className="text-primary">Your Project?</span>
            </h2>
            <p className="mt-6 text-text-muted font-body text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
              Contact us today for a fast, professional quote. We&apos;re ready to deliver quality
              solutions across the Western Cape and nationally.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                data-testid="cta-get-quote-btn"
                className="btn-primary border border-primary text-primary px-8 py-4 font-mono text-xs uppercase tracking-widest hover:text-background transition-colors inline-flex items-center justify-center gap-2"
              >
                Get Your Free Quote
                <ArrowRight size={16} />
              </Link>
              <a
                href="tel:+27825697495"
                data-testid="cta-call-btn"
                className="border border-text-muted/30 text-text-main px-8 py-4 font-mono text-xs uppercase tracking-widest hover:border-text-main transition-colors inline-flex items-center justify-center"
              >
                Call 082 569 7495
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
